import { ForwardedRef, ReactNode, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Box,
  BoxProps,
  Input,
  InputGroup,
  InputProps,
  List,
  ListItem,
  Progress,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import { forwardRefGeneric, useOutsideClick, usePropChange } from '@netiva/classifieds-common';

import { TypeaheadComponentName } from '../theme';

type ItemSelector<TItem> = (item: TItem) => void;
type ItemRenderer<TItem> = (item: TItem, onSelect: ItemSelector<TItem>, isHovered: boolean, index: number) => ReactNode;
type ItemContentRenderer<TItem> = (item: TItem, isHovered: boolean, index: number) => ReactNode;

export interface TypeaheadProps<TItem> extends InputProps {
  isLoading?: boolean;
  items: TItem[];
  onItemSelected: ItemSelector<TItem>;
  emptyValueItem?: ReactNode;
  renderItem?: ItemRenderer<TItem>;
  renderItemContent?: ItemContentRenderer<TItem>;
  containerProps?: BoxProps;
}

function TypeaheadInner<TItem>(
  {
    isLoading,
    items,
    onItemSelected,
    value,
    emptyValueItem,
    renderItem,
    renderItemContent,
    children,
    containerProps,
    size,
    ...rest
  }: TypeaheadProps<TItem>,
  ref: ForwardedRef<HTMLInputElement>
) {
  const styles = useMultiStyleConfig(TypeaheadComponentName, { input: rest });

  const [hoveredIndex, setHoveredIndex] = useState<number>();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  if (usePropChange(items)) {
    setHoveredIndex(undefined);
  }

  const inputRef = useRef<HTMLInputElement | null>(null);
  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(ref, () => inputRef.current);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useOutsideClick(wrapperRef, () => {
    setIsDropdownVisible(false);
  });

  const itemContentRenderer: ItemContentRenderer<TItem> = useMemo(
    () => renderItemContent || ((item) => item?.toString()),
    [renderItemContent]
  );

  const itemRenderer: ItemRenderer<TItem> = useMemo(
    () =>
      renderItem ||
      ((item, onSelect, isHovered, index) => {
        return (
          <ListItem key={index} onClick={() => onSelect(item)} sx={styles.item}>
            {itemContentRenderer(item, isHovered, index)}
          </ListItem>
        );
      }),
    [itemContentRenderer, renderItem, styles.item]
  );

  const handleItemSelected = (item: TItem) => {
    onItemSelected(item);
    inputRef.current?.focus();
    setIsDropdownVisible(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    setIsDropdownVisible(true);
    if (e.code === 'ArrowUp') {
      setHoveredIndex((val) => {
        const len = items.length;
        return typeof val === 'undefined' ? items.length - 1 : (((val - 1) % len) + len) % len;
      });
      e.preventDefault();
    }
    if (e.code === 'ArrowDown') {
      setHoveredIndex((val) => {
        return typeof val === 'undefined' ? 0 : (val + 1) % items.length;
      });
      e.preventDefault();
    }
    if (e.code === 'Enter') {
      if (typeof hoveredIndex !== 'undefined') {
        handleItemSelected(items[hoveredIndex]);
        e.preventDefault();
      }
    }
  };

  const hasDropdownContent = isLoading || items.length > 0 || (!value && !!emptyValueItem);

  const handleBlur = (e: React.FocusEvent) => {
    if (wrapperRef.current && wrapperRef.current.contains(e.relatedTarget)) {
      return;
    }

    setIsDropdownVisible(false);
  };

  return (
    <Box ref={wrapperRef} width="full" {...containerProps} position="relative">
      <InputGroup size={size}>
        <Input
          sx={styles.input}
          ref={inputRef}
          type="text"
          autoComplete="off"
          onFocus={() => setIsDropdownVisible(true)}
          onClick={() => setIsDropdownVisible(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          value={value}
          {...rest}
        />
        {children}
      </InputGroup>
      {isDropdownVisible && hasDropdownContent && (
        <List sx={styles.dropdown} tabIndex={-1}>
          {isLoading && <Progress isIndeterminate />}
          {/* eslint-disable-next-line react-hooks/refs -- not sure why this eslint rule triggers here, might be a bug in eslint? */}
          {items.map((item, index) => itemRenderer(item, handleItemSelected, index === hoveredIndex, index))}
          {!value && emptyValueItem}
        </List>
      )}
    </Box>
  );
}

export const Typeahead = forwardRefGeneric(TypeaheadInner);
